import cn from "classnames";

import { CloseIcon } from "../icons/close.icon";
import { SearchIcon } from "../icons/search.icon";
import { useCallback, useRef, useState } from "react";
import { SEARCH_DEBOUNCE_MS } from "@/app/blu/shop/constants";

export const Search = ({
  id,
  name,
  onChange,
  onReset,
  className,
  inputClassName,
  placeholder,
  defaultValue,
  debounce,
  ...rest
}: SearchProps) => {
  const [search, setSearch] = useState(defaultValue ?? "");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSubmit = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = setTimeout(() => {
        onChange?.(e);
        submitTimeoutRef.current = null;
      }, SEARCH_DEBOUNCE_MS);
    },
    [],
  );
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    const minChars = e.target.value.length > 2;
    if (!minChars) return;
    debounce ? debouncedSubmit(e) : onChange?.(e);
  };
  const handleReset = useCallback(() => {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
    setSearch("");
    const input = searchRef.current;
    if (input) input.value = "";
    onReset && onReset();
  }, [onReset]);
  return (
    <div className={cn("relative", className)}>
      <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
        <SearchIcon width={24} height={24} />
      </div>
      <label htmlFor={id} className="sr-only">
        Search
      </label>
      <input
        ref={searchRef}
        id={id}
        name={name}
        value={search}
        onChange={handleSearchChange}
        placeholder={placeholder}
        className={cn(
          "border-greyscale-strength focus:border-secondary placeholder:body-3 block h-11.25 w-full rounded-full border bg-white px-3 py-1.5 ps-12 focus:outline-none",
          { "pr-8": search },
          inputClassName,
        )}
        {...rest}
      />
      <button
        type={onReset ? "button" : "submit"}
        className={cn(
          "absolute inset-y-0 right-0 flex cursor-pointer items-center px-3 text-gray-500",
          { hidden: !search },
        )}
        onClick={handleReset}
        tabIndex={-1}>
        <CloseIcon width={20} height={20} />
      </button>
    </div>
  );
};

export type SearchProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id?: string;
  name?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReset?: () => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  debounce?: boolean;
};
